import { useProductReportControllerGetProductCardReport } from "@/api/types/main";
import {
  FunnelHistoryDTO,
  ProductAdvertisementHistoryDTO,
  ProductInfoDTO,
  ProfitInfoDTO,
  RatingInfoDTO,
} from "@/api/types/main/schemas";
import { LoadingSpinner } from "@/components/custom-ui/loading.spinner";
import { TanstackTable } from "@/components/custom-ui/tables/tanstack.table";
import {
  MemoizedTanstackTableBody,
  TanstackTableBody,
} from "@/components/custom-ui/tables/tanstack.table.body";
import {
  MemoizedTanstackTableHeader,
  TanstackTableHeader,
} from "@/components/custom-ui/tables/tanstack.table.header";
import { DndSortableHeadersContext } from "@/components/tables/dnd.sortable.headers.context";
import { useCommersCardColumns } from "@/hooks/useCommersCardColumns";
import UseGetFlatRowsTable from "@/hooks/useGetFlatRowsTable";
import { useTableDataRangeStore } from "@/store/table.data.range";
import { TableIds } from "@/store/tables";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { TransformCardData } from "./commers.card.rating";

const REPORT_CARD_READABLE_LABELS: Record<keyof ReportCardProps, string> = {
  acos: "ACOS",
  ctr: "CTR",
  spent: "Расходы",
  basketOrder: "Корзина → заказ",
  basketOrderPercent: "Корзина → заказ, %",
  cardBasket: "Карточка → корзина",
  cardBasketPercent: "Карточка → корзина, %",
  showCard: "Показ → Карточка",
  showCardPercent: "Показ → Карточка, %",
  showLook: "Общее кол-во показов",
  tacos: "TACOS",
  views: "Показы",
  balance: "Баланс",
  revenue: "Выручка",
  ordersFromAds: "Заказы с рекламы",
  budget: "Бюджет",
  clicks: "Клики",
  cr: "CR",
  netProfit: "Чистая прибыль",
  netProfitPercent: "Чистая прибыль, %",
  ordersWithoutReturnsAmount: "Количество заказов без возвратов",
  priceBeforeSpp: "Цена до СПП",
  priceWithSpp: "Цена с СПП",
  productCardRating: "Рейтинг",
  skleikaRating: "Рейтинг в склейке",
  productCardReviewsAmount: "Количество отзывов",
  skleikaReviewsAmount: "Количество отзывов в  склейке",
  stock: "Актуальное остаток",
};

export interface ReportCardData {
  rowLabel: keyof typeof REPORT_CARD_READABLE_LABELS;
  data: TransformCardData[];
}

type ReportCardProps = ProductAdvertisementHistoryDTO &
  ProductInfoDTO &
  ProfitInfoDTO &
  FunnelHistoryDTO &
  RatingInfoDTO;

export const CommersCardReport = () => {
  const dateRange = useTableDataRangeStore((store) => store.dateRange);
  const { id } = useParams<{ id: string }>();
  const productId = Number(id);

  const { data, isLoading, isError, error } = useProductReportControllerGetProductCardReport(
    productId,
    {
      periodEnd: format(dateRange.to, "yyyy-MM-dd"),
      periodStart: format(dateRange.from, "yyyy-MM-dd"),
    },
    {
      query: {
        queryKey: ["card", "report", dateRange.from, dateRange.to, productId],
        select: (data): TransformCardData[] => {
          const mergedData: ReportCardProps = {
            ...data.advertisingInfo,
            ...data.productInfo,
            ...data.profitInfo,
            ...data.purchaseFunnelInfo,
            ...data.ratingInfo,
          };
          const keys = Object.keys(mergedData) as (keyof ReportCardProps)[];
          return keys
            .filter((field) => typeof mergedData[field] === "object")
            .map(
              (field): TransformCardData => ({
                label: REPORT_CARD_READABLE_LABELS[field] || "Значение не добавлено",
                data: mergedData[field as keyof ReportCardProps],
              })
            );
        },
      },
    }
  );

  const columns = useCommersCardColumns({ dateRange: dateRange });

  const { table, columnSizeVars, columnOrder } = UseGetFlatRowsTable({
    data: data || [],
    columns,
    tableId: TableIds.Commers_ReportCard,
  });
  if (isError) return <div>{error?.message}</div>;
  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="h-full flex flex-col">
      <TanstackTable style={{ width: table.getTotalSize(), ...columnSizeVars }} className="flex-1">
        {table.getState().columnSizingInfo.isResizingColumn ? (
          <>
            <MemoizedTanstackTableHeader table={table} columnOrder={columnOrder} sticky="true" />
            <MemoizedTanstackTableBody table={table} columnOrder={columnOrder} />
          </>
        ) : (
          <>
            <DndSortableHeadersContext table={table} tableId={TableIds.Commers_ReportCard}>
              <TanstackTableHeader table={table} columnOrder={columnOrder} sticky="true" />
              <TanstackTableBody table={table} columnOrder={columnOrder} />
            </DndSortableHeadersContext>
          </>
        )}
      </TanstackTable>
    </div>
  );
};
